import { useState, useRef, useEffect } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, FileText, Download, HelpCircle, ChevronDown, X, Maximize2, MinusCircle, PlusCircle } from 'lucide-react'

// Bilder aus der Brunch-Galerie - werden dynamisch aus dem Verzeichnis geladen
// Pfad: /assets/galerie_neu/unterseiten_galerie/brunch_galerie/
const brunchGalleryBasePath = '/assets/galerie_neu/unterseiten_galerie/brunch_galerie/'

// Bild-Modal Component
function ImageModal({ 
  isOpen,
  onClose,
  images,
  currentImageIndex, 
  onImageChange
}: { 
  isOpen: boolean
  onClose: () => void
  images: string[]
  currentImageIndex: number
  onImageChange: (index: number) => void
}) {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentImageIndex > 0) {
        onImageChange(currentImageIndex - 1)
      } else if (e.key === 'ArrowRight' && currentImageIndex < images.length - 1) {
        onImageChange(currentImageIndex + 1)
      } else if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [isOpen, currentImageIndex, images.length, onImageChange, onClose])

  const currentImage = images[currentImageIndex]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-md z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-4 md:inset-8 z-50 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 bg-black/80 backdrop-blur-xl border border-white/10 rounded-t-2xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-brand-800/20 rounded-xl flex items-center justify-center">
                  <Maximize2 className="w-5 h-5 text-brand-400" />
                </div>
                <div>
                  <h3 className="font-serif text-lg md:text-xl text-white">Bildgalerie</h3>
                  <p className="text-white/40 text-sm">
                    {currentImageIndex + 1} von {images.length}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 md:gap-3">
                {/* Navigation */}
                {images.length > 1 && (
                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        onImageChange(Math.max(0, currentImageIndex - 1))
                      }}
                      disabled={currentImageIndex === 0}
                      className="p-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        onImageChange(Math.min(images.length - 1, currentImageIndex + 1))
                      }}
                      disabled={currentImageIndex === images.length - 1}
                      className="p-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </motion.button>
                  </div>
                )}
                
                {/* Close */}
                <motion.button
                  onClick={onClose}
                  className="p-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            
            {/* Image Content */}
            <div className="flex-1 bg-gray-900 border border-white/10 border-t-0 rounded-b-2xl overflow-hidden flex items-center justify-center p-4">
              <img
                src={currentImage}
                alt={`Bild ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// PDF-Dateien - Buffets
const buffetPdfs = [
  {
    name: 'Gutsherren Buffet',
    path: '/assets/pdfs/buffet/gutsherren_buffet.pdf',
    description: 'Gutsherren Buffet'
  },
  {
    name: 'Gutsherren Grill',
    path: '/assets/pdfs/buffet/gutsherren_grill.pdf',
    description: 'Gutsherren Grill'
  },
  {
    name: 'La Dolce Vita',
    path: '/assets/pdfs/buffet/ladolcevita.pdf',
    description: 'La Dolce Vita'
  },
  {
    name: 'Landhaus Buffet',
    path: '/assets/pdfs/buffet/landhaus_buffet.pdf',
    description: 'Landhaus Buffet'
  },
  {
    name: 'Soldatenschmaus',
    path: '/assets/pdfs/buffet/soldatenschmaus.pdf',
    description: 'Soldatenschmaus'
  },
  {
    name: 'Weihnachtstraum',
    path: '/assets/pdfs/buffet/weihnachtstraum.pdf',
    description: 'Weihnachtstraum'
  }
]

// PDF-Dateien - Angebote
const angebotePdfs = [
  {
    name: 'Hochzeit Pauschalangebot',
    path: '/assets/pdfs/angebote/hochzeit_pauschalangebot.pdf',
    description: 'Hochzeit Pauschalangebot'
  },
  {
    name: 'Sektempfang',
    path: '/assets/pdfs/angebote/sektempfang.pdf',
    description: 'Sektempfang'
  },
  {
    name: 'Zusatzleistungen',
    path: '/assets/pdfs/angebote/zusatzleistungen.pdf',
    description: 'Zusatzleistungen'
  }
]

// Download-PDFs (Haupt-PDFs)
const downloadPdfs = {
  buffet: '/assets/hmay_buffets.pdf',
  angebote: '/assets/hmay_angebote.pdf'
}

// Häufig gestellte Fragen
const faqs = [
  {
    question: 'Kann ich das Buffet individuell anpassen oder eigene Wünsche einbringen?',
    answer: 'Ja, Buffets können selbst zusammengestellt werden, und auch Änderungen an den Pauschalangeboten sowie saisonale Ergänzungen (z. B. Spargel, Wild) sind möglich.'
  },
  {
    question: 'Müssen alle Gäste das gleiche Essen wählen und gibt es Optionen für Vegetarier/Veganer?',
    answer: 'Menüs müssen einheitlich für alle Personen zusammengestellt werden, für Vegetarier und Veganer werden jedoch individuelle Gerichte angeboten.'
  },
  {
    question: 'Gibt es eine Mindestteilnehmerzahl für Buffets oder Menüs?',
    answer: 'Die Mindestpersonenzahl beträgt 30 Personen. Bei bestimmten Gerichten oder mehr als zwei Hauptgängen kann eine höhere Mindestzahl gelten oder ein Mindermengenaufschlag berechnet werden.'
  },
  {
    question: 'Darf ich eigene Speisen oder Getränke mitbringen?',
    answer: 'Das Mitbringen ist grundsätzlich nicht möglich. Ausnahmen bestehen lediglich für Hochzeitstorten und Kuchen.'
  },
  {
    question: 'Welche Zusatz- oder Nebenleistungen können über Hückels May gebucht werden?',
    answer: 'Leistungen wie Tischdekoration, Menükarten, Sitzpläne, DJ, Technik, Fotobox oder eine Candy Bar können entweder über das Haus gebucht oder in Eigenregie organisiert werden.'
  },
  {
    question: 'Was ist in den angegebenen Preisen bereits enthalten?',
    answer: 'Die Preise inkludieren Personalkosten für Küche und Service sowie die fertig eingedeckten Tische (inkl. Tischwäsche, Kerzen, Kerzenständer und Gläser).'
  },
  {
    question: 'Wann findet die Detailabsprache für die Veranstaltung statt?',
    answer: 'Die individuelle Planung erfolgt spätestens vier Wochen vor dem Termin, woraufhin eine genaue Kostenübersicht erstellt wird.'
  },
  {
    question: 'Bis wann muss ich die endgültige Personenanzahl melden?',
    answer: 'Die genaue Anzahl muss spätestens 7 Tage vor der Veranstaltung mitgeteilt werden; spätere Verringerungen der Gästezahl können nicht mehr berücksichtigt werden.'
  },
  {
    question: 'Gibt es eine Sperrzeit oder eine Begrenzung der Feierdauer?',
    answer: 'Es gibt keine Sperrzeit, Gäste dürfen so lange feiern, wie sie möchten.'
  },
  {
    question: 'Dürfen wir Luftballons steigen lassen?',
    answer: 'In der Scheune ist dies untersagt. Für den Außenbereich ist aufgrund der Einflugschneise zweier Flughäfen eine rechtzeitige Genehmigung des Ordnungsamtes erforderlich.'
  },
  {
    question: 'Sind die genannten Preise bereits verbindlich?',
    answer: 'Alle Preise sind freibleibend und werden erst mit der detaillierten Veranstaltungsabsprache verbindlich.'
  }
]

// Modal PDF Viewer
function PDFModal({ 
  isOpen,
  onClose,
  pdfs,
  currentPdfIndex, 
  onPdfChange,
  downloadPath
}: { 
  isOpen: boolean
  onClose: () => void
  pdfs: Array<{ name: string; path: string; description: string }>
  currentPdfIndex: number
  onPdfChange: (index: number) => void
  downloadPath: string
}) {
  // Keyboard Navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentPdfIndex > 0) {
        onPdfChange(currentPdfIndex - 1)
      } else if (e.key === 'ArrowRight' && currentPdfIndex < pdfs.length - 1) {
        onPdfChange(currentPdfIndex + 1)
      } else if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [isOpen, currentPdfIndex, pdfs.length, onPdfChange, onClose])

  const currentPdf = pdfs[currentPdfIndex]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-md z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-4 md:inset-8 z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 bg-black/80 backdrop-blur-xl border border-white/10 rounded-t-2xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-brand-800/20 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-brand-400" />
                </div>
                <div>
                  <h3 className="font-serif text-lg md:text-xl text-white">{currentPdf.name}</h3>
                  <p className="text-white/40 text-sm">
                    {currentPdfIndex + 1} von {pdfs.length}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 md:gap-3">
                {/* Navigation */}
                {pdfs.length > 1 && (
                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        onPdfChange(Math.max(0, currentPdfIndex - 1))
                      }}
                      disabled={currentPdfIndex === 0}
                      className="p-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        onPdfChange(Math.min(pdfs.length - 1, currentPdfIndex + 1))
                      }}
                      disabled={currentPdfIndex === pdfs.length - 1}
                      className="p-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </motion.button>
                  </div>
                )}
                
                {/* Download */}
                <a
                  href={downloadPath}
                  download
                  onClick={(e) => e.stopPropagation()}
                  className="hidden sm:flex items-center gap-2 px-4 py-2 bg-brand-800/20 border border-brand-800/30 text-brand-400 rounded-lg hover:bg-brand-800/30 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </a>
                
                {/* Close */}
                <motion.button
                  onClick={onClose}
                  className="p-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            
            {/* PDF Content */}
            <div className="flex-1 bg-gray-900 border border-white/10 border-t-0 rounded-b-2xl overflow-hidden">
              <iframe
                src={`${currentPdf.path}#toolbar=0&navpanes=0&scrollbar=1&zoom=page-fit`}
                className="w-full h-full"
                title={currentPdf.name}
                style={{ border: 'none' }}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default function BuffetsAngebote() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])
  
  // PDF Modal States
  const [isBuffetModalOpen, setIsBuffetModalOpen] = useState(false)
  const [isAngebotModalOpen, setIsAngebotModalOpen] = useState(false)
  const [selectedBuffet, setSelectedBuffet] = useState(0)
  const [selectedAngebot, setSelectedAngebot] = useState(0)
  
  // Image Gallery States
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [galleryImages, setGalleryImages] = useState<string[]>([])
  
  // FAQ State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)

  // Lade Bilder aus dem brunch_galerie Verzeichnis
  useEffect(() => {
    // Liste der Bilder aus dem brunch_galerie Verzeichnis
    const images = [
      `${brunchGalleryBasePath}IMG_5375-001.JPG.JPG`,
      `${brunchGalleryBasePath}IMG_5377-001.JPG.JPG`,
      `${brunchGalleryBasePath}IMG_5436.JPG.JPG`,
      `${brunchGalleryBasePath}IMG_5442.JPG.JPG`,
      `${brunchGalleryBasePath}IMG_5451.JPG.JPG`,
    ]
    setGalleryImages(images.filter(img => img)) // Filtert leere Einträge
  }, [])

  return (
    <div className="bg-[#0a0a0a]">
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[60vh] min-h-[500px] max-h-[700px] w-full overflow-hidden">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/brunchero.jpeg"
            alt="Buffets & Angebote"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.25)' }}
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0a0a]" />
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-brand-800/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-brand-900/10 rounded-full blur-[120px]" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-20"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mb-6"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/[0.07] backdrop-blur-xl border border-white/20 rounded-full shadow-lg shadow-black/20">
              <FileText className="w-4 h-4 text-brand-400" />
              <span className="text-white/80 text-xs uppercase tracking-[0.3em] font-medium">Angebote</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-light tracking-tight leading-[1.1] mb-6"
          >
            Buffets & <span className="italic text-brand-300">Angebote</span>
          </motion.h1>

          <motion.div 
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="h-px w-32 bg-gradient-to-r from-transparent via-brand-800 to-transparent mb-6"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/70 text-base md:text-lg max-w-2xl mb-8 leading-relaxed"
          >
            Entdecken Sie unsere vielfältigen Buffetvorschläge und Pauschalangebote für Ihre perfekte Veranstaltung
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <ChevronDown className="w-6 h-6 text-white/40 animate-bounce" />
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          INTRO TEXT SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-900/5 to-transparent" />
        
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 bg-brand-800/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-brand-400" />
                </div>
                <h3 className="font-serif text-xl text-white mb-2">Vielfältige Auswahl</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Verschiedene Buffets & individuelle Zusammenstellungen
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 bg-brand-800/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Download className="w-6 h-6 text-brand-400" />
                </div>
                <h3 className="font-serif text-xl text-white mb-2">Pauschalangebote</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Kostensicherheit für Ihre Budgetplanung
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 bg-brand-800/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <HelpCircle className="w-6 h-6 text-brand-400" />
                </div>
                <h3 className="font-serif text-xl text-white mb-2">Saisonale Variationen</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Wild, Spargel, Fisch und frische Früchte je nach Jahreszeit
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/10 rounded-2xl p-8 md:p-12"
            >
              <div className="max-w-3xl mx-auto space-y-6">
                <div>
                  <h2 className="font-serif text-2xl md:text-3xl text-white font-light mb-4">
                    Über unsere Buffets & Angebote
                  </h2>
                  <div className="h-px w-20 bg-gradient-to-r from-brand-800 to-transparent mb-6" />
                </div>
                
                <p className="text-white/80 leading-relaxed text-base md:text-lg">
                  Die folgenden Buffetvorschläge dienen als Anregung und stellen nur eine Auswahl unserer vielfältigen Möglichkeiten dar. 
                  Natürlich können wir für Sie auch andere Zusammenstellungen arrangieren. Bitte sprechen Sie uns hierauf bei Bedarf gerne an.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4 pt-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-800/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-brand-400 text-sm font-bold">30+</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Mindestpersonenzahl</h4>
                      <p className="text-white/60 text-sm">Ab 80 Personen sind auch 3 Hauptgerichte möglich</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-800/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText className="w-4 h-4 text-brand-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Individuelle Angebote</h4>
                      <p className="text-white/60 text-sm">Wir erstellen Ihnen ein maßgeschneidertes Angebot</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-white/70 text-sm leading-relaxed">
                    Je nach Jahreszeit bieten wir Ihnen entsprechende Zutaten-Variationen wie Wild, Geflügel, Fisch und Meeresfrüchte sowie 
                    frischen Spargel, Pilze, Gemüse und frische Früchte. Zur genauen Kalkulation bieten wir Ihnen auch Pauschalangebote und 
                    Getränkepauschalen an – für Kostensicherheit bei Ihrer individuellen Budgetplanung.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          BILDGALERIE SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24 border-t border-white/5">
        <div className="absolute top-20 left-10 w-[400px] h-[400px] bg-brand-800/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-brand-900/5 rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-6"
            >
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em]">Galerie</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mb-4"
            >
              Unsere Buffets & <span className="italic text-brand-300">Angebote</span>
            </motion.h2>
            <motion.div 
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="h-px w-24 bg-gradient-to-r from-transparent via-brand-800 to-transparent mx-auto"
            />
          </div>

          {/* Bild Grid */}
          {galleryImages.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              {galleryImages.map((image, index) => {
                // Erstes Bild größer darstellen
                const isFirstImage = index === 0
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    className={`relative group overflow-hidden rounded-2xl border border-white/10 hover:border-brand-800/30 transition-all duration-300 cursor-pointer flex items-center justify-center ${
                      isFirstImage ? 'md:col-span-2 lg:col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedImageIndex(index)
                      setIsImageModalOpen(true)
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-800/10 to-brand-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                    <img
                      src={image}
                      alt={`Buffets & Angebote ${index + 1}`}
                      className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        // Fallback wenn Bild nicht gefunden wird
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                      <div className="w-10 h-10 bg-black/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Maximize2 className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center py-12"
            >
              <p className="text-white/40">Bilder werden geladen...</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          BUFFETS & ANGEBOTE SECTION - Nebeneinander
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24">
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-40 left-20 w-[500px] h-[500px] bg-brand-900/5 rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-6"
            >
              <FileText className="w-4 h-4 text-brand-400" />
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em]">Dokumente</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mb-4"
            >
              Buffets & <span className="italic text-brand-300">Angebote</span>
            </motion.h2>
            <motion.div 
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="h-px w-24 bg-gradient-to-r from-transparent via-brand-800 to-transparent mx-auto mb-6"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-white/60 text-lg max-w-2xl mx-auto"
            >
              Klicken Sie auf eine Kategorie, um die PDFs anzusehen
            </motion.p>
          </div>

          {/* Zwei Spalten Layout */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* BUFFETS - Links */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-800/10 to-brand-900/10 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500" />
              
              <div className="relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-3xl p-8 hover:border-brand-800/30 transition-all duration-300">
                {/* Header */}
                <div className="mb-8">
                  <div className="w-14 h-14 bg-brand-800/20 rounded-2xl flex items-center justify-center mb-4">
                    <FileText className="w-7 h-7 text-brand-400" />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-white font-light mb-3">
                    Buffet<span className="italic text-brand-300">auswahl</span>
                  </h3>
                  <div className="h-px w-16 bg-gradient-to-r from-brand-800 to-transparent mb-4" />
                  <p className="text-white/60 leading-relaxed">
                    Entdecken Sie unsere vielfältigen Buffetvorschläge für jeden Anlass
                  </p>
                </div>

                {/* PDF Liste */}
                <div className="space-y-3 mb-6">
                  {buffetPdfs.map((pdf, index) => (
                    <motion.button
                      key={index}
                      onClick={() => {
                        setSelectedBuffet(index)
                        setIsBuffetModalOpen(true)
                      }}
                      className="w-full flex items-center justify-between gap-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl hover:bg-white/[0.05] hover:border-brand-800/30 transition-all group/item"
                      whileHover={{ scale: 1.02, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-brand-800/20 rounded-lg flex items-center justify-center group-hover/item:bg-brand-800/30 transition-colors">
                          <FileText className="w-4 h-4 text-brand-400" />
                        </div>
                        <span className="text-white/80 font-medium group-hover/item:text-white transition-colors">
                          {pdf.name}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/40 group-hover/item:text-brand-400 transition-colors" />
                    </motion.button>
                  ))}
                </div>

                {/* Download Button */}
                <a
                  href={downloadPdfs.buffet}
                  download
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-brand-800/20 border border-brand-800/30 text-brand-300 rounded-xl hover:bg-brand-800/30 transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  <span className="font-medium">Alle Buffets downloaden</span>
                </a>
              </div>
            </motion.div>

            {/* ANGEBOTE - Rechts */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-800/10 to-brand-900/10 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500" />
              
              <div className="relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-3xl p-8 hover:border-brand-800/30 transition-all duration-300">
                {/* Header */}
                <div className="mb-8">
                  <div className="w-14 h-14 bg-brand-800/20 rounded-2xl flex items-center justify-center mb-4">
                    <FileText className="w-7 h-7 text-brand-400" />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-white font-light mb-3">
                    Angebote & <span className="italic text-brand-300">Zusatzleistungen</span>
                  </h3>
                  <div className="h-px w-16 bg-gradient-to-r from-brand-800 to-transparent mb-4" />
                  <p className="text-white/60 leading-relaxed">
                    Pauschalangebote und Zusatzleistungen für Ihre Veranstaltung
                  </p>
                </div>

                {/* PDF Liste */}
                <div className="space-y-3 mb-6">
                  {angebotePdfs.map((pdf, index) => (
                    <motion.button
                      key={index}
                      onClick={() => {
                        setSelectedAngebot(index)
                        setIsAngebotModalOpen(true)
                      }}
                      className="w-full flex items-center justify-between gap-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl hover:bg-white/[0.05] hover:border-brand-800/30 transition-all group/item"
                      whileHover={{ scale: 1.02, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-brand-800/20 rounded-lg flex items-center justify-center group-hover/item:bg-brand-800/30 transition-colors">
                          <FileText className="w-4 h-4 text-brand-400" />
                        </div>
                        <span className="text-white/80 font-medium group-hover/item:text-white transition-colors">
                          {pdf.name}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/40 group-hover/item:text-brand-400 transition-colors" />
                    </motion.button>
                  ))}
                </div>

                {/* Download Button */}
                <a
                  href={downloadPdfs.angebote}
                  download
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-brand-800/20 border border-brand-800/30 text-brand-300 rounded-xl hover:bg-brand-800/30 transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  <span className="font-medium">Alle Angebote downloaden</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PDF Modals */}
      <PDFModal
        isOpen={isBuffetModalOpen}
        onClose={() => setIsBuffetModalOpen(false)}
        pdfs={buffetPdfs}
        currentPdfIndex={selectedBuffet}
        onPdfChange={setSelectedBuffet}
        downloadPath={downloadPdfs.buffet}
      />
      
      <PDFModal
        isOpen={isAngebotModalOpen}
        onClose={() => setIsAngebotModalOpen(false)}
        pdfs={angebotePdfs}
        currentPdfIndex={selectedAngebot}
        onPdfChange={setSelectedAngebot}
        downloadPath={downloadPdfs.angebote}
      />

      {/* Image Gallery Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        images={galleryImages}
        currentImageIndex={selectedImageIndex}
        onImageChange={setSelectedImageIndex}
      />

      {/* ════════════════════════════════════════════════════════════
          FAQ SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-32 border-t border-white/5">
        <div className="absolute bottom-40 left-20 w-[600px] h-[600px] bg-brand-900/5 rounded-full blur-[150px]" />
        <div className="absolute top-40 right-20 w-[400px] h-[400px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-6"
            >
              <HelpCircle className="w-4 h-4 text-brand-400" />
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em]">FAQ</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mb-4"
            >
              Häufig gestellte <span className="italic text-brand-300">Fragen</span>
            </motion.h2>
            <motion.div 
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="h-px w-24 bg-gradient-to-r from-transparent via-brand-800 to-transparent mx-auto mb-6"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-white/60 text-lg max-w-2xl mx-auto"
            >
              Finden Sie Antworten auf die wichtigsten Fragen rund um unsere Buffets und Angebote
            </motion.p>
          </div>

          {/* FAQ Accordion - Single Column für bessere Lesbarkeit */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="group"
              >
                <div className={`relative bg-gradient-to-br from-white/[0.03] to-white/[0.01] border rounded-2xl overflow-hidden transition-all duration-300 ${
                  openFaqIndex === index 
                    ? 'border-brand-800/50 shadow-lg shadow-brand-800/5' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full p-6 text-left flex items-start justify-between gap-6 group/button"
                  >
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                          openFaqIndex === index 
                            ? 'bg-brand-800/20 text-brand-400' 
                            : 'bg-white/5 text-white/40 group-hover/button:bg-white/10 group-hover/button:text-white/60'
                        }`}>
                          <span className="text-sm font-semibold">{index + 1}</span>
                        </div>
                        <h3 className={`font-serif text-lg md:text-xl leading-relaxed transition-colors duration-300 ${
                          openFaqIndex === index 
                            ? 'text-brand-300' 
                            : 'text-white group-hover/button:text-white/90'
                        }`}>
                          {faq.question}
                        </h3>
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: openFaqIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="flex-shrink-0 mt-1"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        openFaqIndex === index 
                          ? 'bg-brand-800/20' 
                          : 'bg-white/5 group-hover/button:bg-white/10'
                      }`}>
                        <ChevronDown className={`w-5 h-5 transition-colors duration-300 ${
                          openFaqIndex === index 
                            ? 'text-brand-400' 
                            : 'text-white/40 group-hover/button:text-white/60'
                        }`} />
                      </div>
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {openFaqIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-2">
                          <div className="pl-11 pr-8">
                            <div className="h-px w-full bg-gradient-to-r from-brand-800/50 via-brand-800/20 to-transparent mb-4" />
                            <p className="text-white/80 leading-relaxed text-base">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-16 text-center"
          >
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-brand-800/20 blur-2xl rounded-full" />
              <div className="relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-8 md:p-12">
                <HelpCircle className="w-12 h-12 text-brand-400 mx-auto mb-4" />
                <h3 className="font-serif text-2xl md:text-3xl text-white font-light mb-3">
                  Noch Fragen offen?
                </h3>
                <p className="text-white/70 mb-6 max-w-md mx-auto">
                  Unser Team berät Sie gerne persönlich und erstellt Ihnen ein individuelles Angebot
                </p>
                <a
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-brand-800/20 border border-brand-800/30 text-brand-300 rounded-xl hover:bg-brand-800/30 transition-all duration-300 hover:scale-105"
                >
                  <span className="font-medium">Jetzt Kontakt aufnehmen</span>
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

